import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { disciplineLabel, FishingDiscipline } from "../../types/fishingDiscipline";
import { getUpcomingCompetitions, UpcomingCompetition } from "../../services/upcomingCompetitions";

const Arrow = () => <span aria-hidden="true">↗</span>;

const UpcomingCompetitions = () => {
  const [races, setRaces] = useState<UpcomingCompetition[]>([]);
  const [loading, setLoading] = useState(true);
  const [failedDisciplines, setFailedDisciplines] = useState(0);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.allSettled([
      getUpcomingCompetitions(FishingDiscipline.TroutArea),
      getUpcomingCompetitions(FishingDiscipline.CarpAndCrucian),
    ]).then(results => {
      if (!active) return;
      const available = results.flatMap(result => result.status === "fulfilled" ? result.value : []);
      setRaces(available.sort((a, b) =>
        a.competitionDate.localeCompare(b.competitionDate) || a.id - b.id
      ).slice(0, 3));
      setFailedDisciplines(results.filter(result => result.status === "rejected").length);
      setLoading(false);
    });
    return () => { active = false; };
  }, [attempt]);

  return (
    <section className="home-races" id="prossime-gare" aria-labelledby="races-title">
      <div className="home-container home-races__layout">
        <div className="home-races__intro">
          <p className="home-races__eyebrow">La passione si mette in gioco</p>
          <h2 id="races-title">Ci vediamo alla<br />prossima gara.</h2>
          <p>Trout Area o Carpe e Carassi: scopri i prossimi appuntamenti e vivi il piacere della sfida.</p>
          <nav className="home-races__calendars" aria-label="Calendari delle gare">
            <Link to="/gare">Calendario Trout Area <span aria-hidden="true">→</span></Link>
            <Link to="/carpe/gare">Calendario Carpe e Carassi <span aria-hidden="true">→</span></Link>
          </nav>
        </div>
        <div className="home-races__schedule" aria-busy={loading}>
          <h3 className="home-races__list-title">Prossimi appuntamenti</h3>
          {loading ? (
            <div className="home-races__loading" role="status">
              <p>Caricamento delle prossime gare…</p>
              {[1, 2, 3].map(item => <div key={item} className="home-races__skeleton" aria-hidden="true" />)}
            </div>
          ) : (
            <>
              {races.length > 0 && (
                <ol className="home-race-list">
                  {races.map(race => {
                    const day = race.competitionDate.slice(0, 10);
                    const date = new Date(`${day}T12:00:00Z`);
                    const calendar = race.discipline === FishingDiscipline.CarpAndCrucian ? "/carpe/gare" : "/gare";
                    return (
                      <li className="home-race" key={`${race.discipline}-${race.id}`}>
                        <time dateTime={day} className="home-race__date" aria-label={date.toLocaleDateString("it-IT", { dateStyle: "long", timeZone: "Europe/Rome" })}>
                          <strong>{day.slice(8, 10)}</strong>
                          <span>{date.toLocaleDateString("it-IT", { month: "short", timeZone: "Europe/Rome" })} {day.slice(0, 4)}</span>
                        </time>
                        <div className="home-race__body">
                          <p className="home-race__discipline">{disciplineLabel(race.discipline)}</p>
                          <h4>{race.name}</h4>
                          {race.location && <p className="home-race__location">{race.location}</p>}
                          <Link to={calendar} aria-label={`Vedi gara e locandina: ${race.name}`}>
                            Vedi gara e locandina <span aria-hidden="true">→</span>
                          </Link>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
              {races.length === 0 && (
                <div className="home-races__message" role="status">
                  <h4>{failedDisciplines === 2 ? "Il calendario non è disponibile." : failedDisciplines === 1 ? "Calendario parzialmente disponibile." : "In attesa della prossima sfida."}</h4>
                  <p>{failedDisciplines > 0
                    ? "Non riusciamo a caricare tutti gli appuntamenti. Puoi riprovare o contattarci per informazioni sulle prossime gare."
                    : "Al momento non ci sono gare in programma. Esplora i calendari e le classifiche oppure contattaci per i prossimi appuntamenti."}</p>
                  <Link to="/contatti" className="home-text-link">Contatta l'associazione <Arrow /></Link>
                </div>
              )}
              {failedDisciplines > 0 && (
                <div className="home-races__retry" role="status">
                  {races.length > 0 && <p>Gli appuntamenti di una disciplina non sono disponibili.</p>}
                  <button type="button" onClick={() => setAttempt(value => value + 1)}>Riprova a caricare</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default UpcomingCompetitions;
